import { z } from 'zod';

import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material';
import { EntityType } from '@nohara-dxdev/mt-common';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { useCreateTask } from '../api';

const createTaskSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
});

type CreateTaskForm = z.infer<typeof createTaskSchema>;

export interface CreateTaskDialogProps {
  open: boolean;
  onClose: () => void;
  entityId: string;
  entityType: EntityType;
}

export const CreateTaskDialog = ({
  open,
  onClose,
  entityId,
  entityType,
}: CreateTaskDialogProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createTaskMutation = useCreateTask();

  const form = useForm<CreateTaskForm>({
    resolver: zodResolver(createTaskSchema),
  });

  const { reset } = form;

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: CreateTaskForm) => {
    if (entityType === 'task') {
      return;
    }
    setIsSubmitting(true);
    try {
      await createTaskMutation.mutateAsync({
        ...data,
        entityId,
        entityType,
      });
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('tasks.create.title')}</DialogTitle>
      <FormContainer formContext={form} onSuccess={onSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextFieldElement
              name="title"
              label={t('tasks.create.titleLabel')}
              fullWidth
              required
              disabled={isSubmitting}
            />
            <TextFieldElement
              name="description"
              label={t('tasks.create.descriptionLabel')}
              fullWidth
              multiline
              rows={4}
              required
              disabled={isSubmitting}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {t('tasks.create.button')}
          </Button>
        </DialogActions>
      </FormContainer>
    </Dialog>
  );
};
